import { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Snackbar from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";
import { useApiService } from "../../shared/apiservice";
import { Attachment } from "../../shared/types";
import { UploadFileOutlined } from "@mui/icons-material";

type AttachmentsGalleryProps = {
    attachments: Attachment[];
    title?: string;
};

export default function AttachmentsGallery({ attachments, title }: AttachmentsGalleryProps) {
    const apiService = useApiService();
    const [downloadingKey, setDownloadingKey] = useState<string | null>(null);
    const [snackMessage, setSnackMessage] = useState<string | null>(null);
    const [viewerOpen, setViewerOpen] = useState(false);
    const [viewerIndex, setViewerIndex] = useState(0);
    const [resolvedUrls, setResolvedUrls] = useState<Record<string, string>>({});

    const imageAttachments = useMemo(
        () => attachments.filter((a) => (a.mime_type ?? "").toLowerCase().startsWith("image/")),
        [attachments]
    );

    const hasImages = imageAttachments.length > 0;
    const currentImage = hasImages ? imageAttachments[viewerIndex] : null;
    const currentImageUrl = currentImage ? (resolvedUrls[currentImage.key] ?? currentImage.url) : null;
    const isCurrentImagePending = currentImage ? currentImage.status !== "complete" : false;

    useEffect(() => {
        let cancelled = false;

        async function prefetchMissingImageUrls() {
            const missing = imageAttachments
                .filter((a) => a.status === "complete" && !a.url && !resolvedUrls[a.key])
                .slice(0, 12);

            if (missing.length === 0) return;

            await Promise.all(
                missing.map(async (a) => {
                    try {
                        const response = await apiService.get(`/attachments/${a.key}`);
                        if (!response.ok) return;
                        const data: Attachment = await response.json();
                        if (data.status === "complete" && data.url && !cancelled) {
                            setResolvedUrls((prev) => (prev[a.key] ? prev : { ...prev, [a.key]: data.url! }));
                        }
                    } catch {
                        // ignore prefetch failures; download still works
                    }
                })
            );
        }

        prefetchMissingImageUrls();
        return () => {
            cancelled = true;
        };
    }, [apiService, imageAttachments, resolvedUrls]);

    async function downloadAttachment(attachment: Attachment) {
        setDownloadingKey(attachment.key);
        try {
            const response = await apiService.get(`/attachments/${attachment.key}`);
            if (!response.ok) {
                setSnackMessage("Failed to get download link. Please try again.");
                return;
            }
            const data: Attachment = await response.json();
            if (data.status === "complete" && data.url) {
                window.open(data.url, "_blank");
            } else {
                setSnackMessage(`File "${attachment.filename}" is unavailable. It may still be processing.`);
            }
        } catch {
            setSnackMessage("Failed to get download link. Please try again.");
        } finally {
            setDownloadingKey(null);
        }
    }

    function openViewerForIndex(idx: number) {
        if (!hasImages) return;
        setViewerIndex(Math.min(Math.max(idx, 0), imageAttachments.length - 1));
        setViewerOpen(true);
    }

    function prevImage() {
        if (!hasImages) return;
        setViewerIndex((i) => (i - 1 + imageAttachments.length) % imageAttachments.length);
    }

    function nextImage() {
        if (!hasImages) return;
        setViewerIndex((i) => (i + 1) % imageAttachments.length);
    }

    if (attachments.length === 0) return null;

    return (
        <>
            {title ? (
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    {title}
                </Typography>
            ) : null}

            {hasImages ? (
                <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} sx={{ mb: 1 }}>
                    {imageAttachments.map((a, idx) => {
                        const src = resolvedUrls[a.key] ?? a.url;
                        const isPending = a.status !== "complete";
                        return (
                            <Card
                                key={a.key}
                                variant="outlined"
                                sx={{
                                    width: 120,
                                    height: 120,
                                    overflow: "hidden",
                                    borderRadius: 2,
                                }}
                            >
                                <CardActionArea
                                    onClick={() => openViewerForIndex(idx)}
                                    sx={{ width: "100%", height: "100%" }}
                                >
                                    {isPending ? (
                                        <Box
                                            sx={{
                                                width: "100%",
                                                height: "100%",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                flexDirection: "column",
                                                gap: 0.5,
                                                bgcolor: "action.hover",
                                            }}
                                        >
                                            <UploadFileOutlined />
                                            <Typography variant="caption" color="text.secondary">
                                                Pending
                                            </Typography>
                                        </Box>
                                    ) : src ? (
                                        <Box
                                            component="img"
                                            src={src}
                                            alt={a.filename}
                                            loading="lazy"
                                            sx={{
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "cover",
                                                display: "block",
                                                bgcolor: "action.hover",
                                            }}
                                        />
                                    ) : (
                                        <Box
                                            sx={{
                                                width: "100%",
                                                height: "100%",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                bgcolor: "action.hover",
                                            }}
                                        >
                                            <InsertDriveFileRoundedIcon color="action" />
                                        </Box>
                                    )}
                                </CardActionArea>
                            </Card>
                        );
                    })}
                </Stack>
            ) : null}

            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
                {attachments
                    .filter((a) => a.status === "complete")
                    .map((a) => (
                        <Button
                            key={a.key}
                            size="small"
                            variant="outlined"
                            startIcon={
                                downloadingKey === a.key ? (
                                    <CircularProgress size={16} />
                                ) : (a.mime_type ?? "").toLowerCase().startsWith("image/") ? (
                                    <DownloadRoundedIcon fontSize="small" />
                                ) : (
                                    <InsertDriveFileRoundedIcon fontSize="small" />
                                )
                            }
                            onClick={() => downloadAttachment(a)}
                            disabled={downloadingKey === a.key}
                            sx={{ textTransform: "none" }}
                        >
                            Download
                        </Button>
                    ))}
            </Stack>

            <Dialog
                open={viewerOpen}
                onClose={() => setViewerOpen(false)}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle sx={{ pr: 6 }}>
                    <Typography variant="subtitle1" fontWeight={600} noWrap>
                        {currentImage?.filename ?? "Preview"}
                    </Typography>
                    <IconButton
                        aria-label="Close"
                        onClick={() => setViewerOpen(false)}
                        sx={{ position: "absolute", right: 8, top: 8 }}
                    >
                        <CloseRoundedIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    <Box
                        sx={{
                            position: "relative",
                            bgcolor: "black",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: 420,
                        }}
                    >
                        {isCurrentImagePending ? (
                            <Stack alignItems="center" spacing={1} sx={{ p: 3 }}>
                                <UploadFileOutlined />
                                <Typography color="white">Attachment pending…</Typography>
                            </Stack>
                        ) : currentImageUrl ? (
                            <Box
                                component="img"
                                src={currentImageUrl ?? undefined}
                                alt={currentImage?.filename ?? "Attachment"}
                                sx={{
                                    maxWidth: "100%",
                                    maxHeight: "75vh",
                                    objectFit: "contain",
                                    display: "block",
                                }}
                            />
                        ) : (
                            <Typography color="white" sx={{ p: 3 }}>
                                Preview unavailable. Use download to open this attachment.
                            </Typography>
                        )}

                        {hasImages && imageAttachments.length > 1 ? (
                            <>
                                <IconButton
                                    aria-label="Previous image"
                                    onClick={prevImage}
                                    sx={{
                                        position: "absolute",
                                        left: 8,
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        color: "white",
                                        bgcolor: "rgba(0,0,0,0.35)",
                                        "&:hover": { bgcolor: "rgba(0,0,0,0.55)" },
                                    }}
                                >
                                    <ArrowBackIosNewRoundedIcon />
                                </IconButton>
                                <IconButton
                                    aria-label="Next image"
                                    onClick={nextImage}
                                    sx={{
                                        position: "absolute",
                                        right: 8,
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        color: "white",
                                        bgcolor: "rgba(0,0,0,0.35)",
                                        "&:hover": { bgcolor: "rgba(0,0,0,0.55)" },
                                    }}
                                >
                                    <ArrowForwardIosRoundedIcon />
                                </IconButton>
                            </>
                        ) : null}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ justifyContent: "space-between" }}>
                    <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
                        {hasImages ? `${viewerIndex + 1} of ${imageAttachments.length}` : null}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                        {currentImage && currentImage.status === "complete" ? (
                            <Button
                                size="small"
                                startIcon={
                                    downloadingKey === currentImage.key ? (
                                        <CircularProgress size={16} />
                                    ) : (
                                        <DownloadRoundedIcon fontSize="small" />
                                    )
                                }
                                disabled={downloadingKey === currentImage.key}
                                onClick={() => downloadAttachment(currentImage)}
                            >
                                Download
                            </Button>
                        ) : null}
                        <Button size="small" onClick={() => setViewerOpen(false)}>
                            Close
                        </Button>
                    </Stack>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackMessage !== null}
                autoHideDuration={4000}
                onClose={() => setSnackMessage(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity="warning" onClose={() => setSnackMessage(null)} variant="filled">
                    {snackMessage}
                </Alert>
            </Snackbar>
        </>
    );
}

